import {FC, useContext, useLayoutEffect, useMemo, useRef, useState} from 'react';
import {
  forEach, isArray, isBoolean, isEmpty, isNil, isString,
  map, omit, pickBy, reject, size, some, sortBy, uniqBy
} from 'lodash';
import {Checkbox, parseState, stringifyState, withRouter} from 'apstra-ui-common';
import {Popup} from 'semantic-ui-react';

import UserStoreContext from '../../../../UserStoreContext';
import {NODE_ROLES} from '../../../../roles';
import {TopologySettingsType, TopologySettingsField} from '../types';

import './TopologySettings.less';
import DropdownWithCheckboxes from '../../../DropdownWithCheckboxes';

const TOPOLOGY_SETTING_QUERY: Record<keyof TopologySettingsType, string> = {
  showLinks: 'show-links',
  expandNodes: 'expand-nodes',
  expandExternalGenerics: 'expand-external-generics',
  toggledNodes: 'toggled-nodes',
  showSpines: 'show-spines',
  showSuperspines: 'show-superspines',
  showLeafs: 'show-leafs',
};

const DEFAULT_SETTINGS: TopologySettingsType = {
  expandExternalGenerics: true,
  expandNodes: false,
  showLinks: false,
  toggledNodes: [],
  showSpines: false,
  showSuperspines: false,
  showLeafs: false,
};

const BOOLEAN_TOPOLOGY_SETTINGS: Array<TopologySettingsField> = [
  'showLinks', 'expandNodes', 'expandExternalGenerics', 'showLeafs', 'showSpines', 'showSuperspines'
];

const parseQueryParams = ({
  location
}) => {
  const searchParams = new URLSearchParams(location.search);
  const settings = {};

  forEach(TOPOLOGY_SETTING_QUERY, (query, field) => {
    if (searchParams.has(query)) {
      const value = searchParams.get(query);
      if (BOOLEAN_TOPOLOGY_SETTINGS.includes(field as TopologySettingsField)) {
        settings[field] = value === 'true';
        return;
      }
      if (field === 'toggledNodes') {
        try {
          const state = parseState(value);
          settings[field] = isArray(state) ? state : [];
        } catch {
          settings[field] = [];
        }
        return;
      }
      settings[field] = value;
    }
  });

  return settings;
};

const useDefaultSettings = ({
  selectedRackId, showLinks, expandNodes, expandExternalGenerics, showNodes, persistSettings, userStoreKey, userStore,
  isCollapsed, location, railsToShowOptions, tooManyLinks,
}) => {
  const storedSettings = userStore.getStoreValue([userStoreKey]);
  let initialSettings = storedSettings ?
    {
      ...storedSettings,
      showLinks: tooManyLinks ? false : storedSettings.showLinks,
    } : {};

  if (isCollapsed) {
    initialSettings.expandNodes = true;
  }
  if (selectedRackId) {
    initialSettings.showLinks = true;
  }
  if (isBoolean(showLinks) && !isBoolean(storedSettings?.showLinks)) {
    initialSettings.showLinks = showLinks;
  }
  if (isBoolean(expandNodes) && !isBoolean(storedSettings?.expandNodes)) {
    initialSettings.expandNodes = expandNodes;
  }
  if (isBoolean(expandExternalGenerics) && !isBoolean(storedSettings?.expandExternalGenerics)) {
    initialSettings.expandExternalGenerics = expandExternalGenerics;
  }
  if (showNodes) {
    initialSettings.showNodes = showNodes;
  }
  if (persistSettings) {
    initialSettings = {...initialSettings, ...parseQueryParams({location})};
  }
  if (selectedRackId) {
    initialSettings.expandNodes = true;
  }
  initialSettings.railsToShow = map(railsToShowOptions, 'value');
  return {
    ...DEFAULT_SETTINGS,
    ...initialSettings
  };
};

export const TopologySettings: FC<any> = withRouter(({
  tooManyLinksThreshold = 1000,
  isCollapsed,
  isFiveStage,
  isGpuCluster,
  showLinks,
  expandNodes,
  expandExternalGenerics,
  userStoreKey,
  // toggledNodes,
  basicSettings,
  hideSettings,
  persistSettings,
  hideExpandNodes,
  showNodes,
  onChange,
  selectedPodId,
  selectedRackId,
  selectedAccessGroupId,
  selectedNodeId,
  hasAccessGroups,
  location,
  navigate,
  nodes,
  links,
  racks,
}) => {
  const {userStore} = useContext(UserStoreContext);
  const showRailDropdown = isGpuCluster && (selectedRackId || racks?.length === 1);
  const railsToShowOptions = useMemo(() => {
    if (!showRailDropdown) return null;
    const allRailOptions = map(links, (link) => ({
      value: link.rail_index,
      text: link.rail?.label ?? link.rail_index
    }));
    const uniqRailOptions = reject(uniqBy(
      allRailOptions,
      (option) => option.value
    ), (option) => isNil(option.value));
    const sortedRailOptions = sortBy(uniqRailOptions, (option) => option.value);
    return map(sortedRailOptions, (railOption) => ({
      ...railOption,
      key: railOption.value,
    }));
  }, [showRailDropdown, links]);
  const linkCount = size(links);
  const tooManyLinks = linkCount > tooManyLinksThreshold;
  const defaultSettings = useDefaultSettings({
    showLinks, selectedRackId, expandNodes, expandExternalGenerics, showNodes, persistSettings, userStoreKey, userStore,
    isCollapsed, location, railsToShowOptions, tooManyLinks,
  });
  const [settings, setSettings] = useState<any>(defaultSettings);
  useLayoutEffect(() => {
    setSettings((v) => ({...v, railsToShow: railsToShowOptions ? map(railsToShowOptions, 'value') : null}));
  }, [railsToShowOptions]);
  useLayoutEffect(() => {
    if (tooManyLinks && settings.showLinks) {
      setSettings((v) => ({...v, showLinks: false}));
    }
  }, [settings, tooManyLinks]);
  const prevSettingsRef = useRef(defaultSettings);
  useLayoutEffect(
    () => {
      onChange?.(settings);

      const settingsUpdates = pickBy(
        settings,
        (value, key) => prevSettingsRef.current[key] !== value
      );

      if (!isEmpty(settingsUpdates)) {
        if (persistSettings) {
          const searchParams = new URLSearchParams(location.search);
          forEach(settingsUpdates, (value, field) => {
            const query = TOPOLOGY_SETTING_QUERY[field];
            if (query) {
              if (isString(value) || isBoolean(value)) {
                searchParams.set(query, value.toString());
              } else if (isArray(value) && !isEmpty(value)) {
                const state = stringifyState(value);
                searchParams.set(query, state);
              } else {
                searchParams.delete(query);
              }
            }
          });
          navigate(`?${searchParams.toString()}`);
        }
        userStore.setFor(
          userStoreKey,
          {
            ...userStore.getStoreValue([userStoreKey]) ?? {},
            ...omit(settings, tooManyLinks ? ['showLinks'] : []),
          }
        );
      }

      prevSettingsRef.current = settings;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onChange, settings]
  );
  const hasExternalGenerics = useMemo(
    () => some(nodes, (node) => node.role === NODE_ROLES.GENERIC && node.external),
    [nodes]
  );
  const visibleSettings = {
    expandNodes: !hideExpandNodes && (!selectedRackId || (hasAccessGroups && !selectedAccessGroupId)),
    expandExternalGenerics: hasExternalGenerics,
    showLinks: true,
    showNodesSpines: selectedRackId && !selectedNodeId && !basicSettings && !isCollapsed && !selectedAccessGroupId,
    showNodesSuperspines: isFiveStage && selectedPodId && !selectedRackId && !selectedNodeId && !basicSettings &&
      !selectedAccessGroupId,
    showNodesLeafs: hasAccessGroups && !!selectedAccessGroupId,
    showRailDropdown,
  };

  return hideSettings ? null : (
    <div className='topology-settings'>
      {visibleSettings.expandNodes &&
        <Checkbox
          label='Expand Nodes?'
          indeterminate={!settings.expandNodes && !isEmpty(settings.toggledNodes)}
          checked={settings.expandNodes}
          onChange={() => setSettings((v) => ({...v, expandNodes: !v.expandNodes}))}
          aria-labelledby={undefined}
        />
      }
      {visibleSettings.expandExternalGenerics &&
        <Checkbox
          label='Expand External Generics?'
          indeterminate={!settings.expandExternalGenerics && !isEmpty(settings.expandExternalGenerics)}
          checked={settings.expandExternalGenerics}
          onChange={() => setSettings((v) => ({...v, expandExternalGenerics: !v.expandExternalGenerics}))}
          aria-labelledby={undefined}
        />
      }
      {visibleSettings.showLinks &&
        <Popup
          content={`Too many links to display (${linkCount})`}
          disabled={!tooManyLinks}
          trigger={(
            <Checkbox
              disabled={tooManyLinks}
              label='Show Links?'
              checked={tooManyLinks ? false : settings.showLinks}
              onChange={() => setSettings((v) => ({...v, showLinks: !v.showLinks}))}
              aria-labelledby={undefined}
            />
          )}
        />
      }
      {visibleSettings.showNodesSpines &&
        <Checkbox
          label='Show Spines?'
          checked={settings.showSpines}
          onChange={() => setSettings((v) => ({...v, showSpines: !v.showSpines}))}
          aria-labelledby={undefined}
        />
      }
      {visibleSettings.showNodesSuperspines &&
        <Checkbox
          label='Show Superspines?'
          checked={settings.showSuperspines}
          onChange={() => setSettings((v) => ({...v, showSuperspines: !v.showSuperspines}))}
          aria-labelledby={undefined}
        />
      }
      {visibleSettings.showNodesLeafs &&
        <Checkbox
          label='Show Leafs?'
          checked={settings.showLeafs}
          onChange={() => setSettings((v) => ({...v, showLeafs: !v.showLeafs}))}
          aria-labelledby={undefined}
        />
      }
      {visibleSettings.showRailDropdown &&
        <DropdownWithCheckboxes
          addControlButtons
          className='rail-filter'
          dropdownLabel='GPU Rails'
          currentSelection={settings.railsToShow ?? []}
          onApplySelection={(railsToShow) => setSettings((v) => ({...v, railsToShow}))}
          items={railsToShowOptions}
        />
      }
    </div>
  );
});

export const withTopologySettings = (Component, settingsProps) => (props) => {
  const {rackPreview} = props;
  const [topologySettings, setTopologySettings] = useState<any>(
    rackPreview ? {expandNodes: true, showLinks: true} : null
  );
  return (
    <>
      {!rackPreview && <TopologySettings
        {...props}
        {...settingsProps}
        onChange={setTopologySettings}
      />}
      {(topologySettings || rackPreview) && <Component
        {...props}
        {...topologySettings}
      />}
    </>
  );
};
